import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Wand2, Save, Eye, ChevronDown, Link as LinkIcon, Copy, HelpCircle, X } from "lucide-react";
import FileUpload from "@/components/FileUpload";
import ThumbnailCropper from "@/components/ThumbnailCropper";

export default function CourseBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [course, setCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [aiGen, setAiGen] = useState({ topic: "", audience: "professionals", duration: "short", notes: "" });
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (isNew) {
      setCourse({
        title: "Untitled course",
        description: "A short description of what learners will get.",
        category: "General",
        price: 0,
        currency: "USD",
        visibility: "org",
        modules: [],
        is_published: false,
        passing_score: 80,
      });
    } else {
      api.get(`/courses/${id}`).then(r => setCourse(r.data)).catch(() => toast.error("Course not found"));
    }
  }, [id, isNew]);

  const save = async ({ publish } = {}) => {
    if (!course) return;
    setSaving(true);
    try {
      if (isNew || !course.course_id) {
        const r = await api.post("/courses", {
          title: course.title, description: course.description,
          thumbnail: course.thumbnail, category: course.category,
          price: course.price, currency: course.currency, visibility: course.visibility,
          passing_score: course.passing_score ?? 80,
        });
        // patch modules
        const created = r.data;
        const update = {
          modules: course.modules,
          is_published: publish ? true : created.is_published,
        };
        const r2 = await api.patch(`/courses/${created.course_id}`, update);
        setCourse(r2.data);
        toast.success(publish ? "Published" : "Saved");
        nav(`/builder/${created.course_id}`, { replace: true });
      } else {
        const update = {
          title: course.title, description: course.description,
          thumbnail: course.thumbnail, category: course.category,
          price: course.price, currency: course.currency, visibility: course.visibility,
          modules: course.modules,
          passing_score: course.passing_score ?? 80,
          ...(publish !== undefined ? { is_published: publish } : {}),
        };
        const r = await api.patch(`/courses/${course.course_id}`, update);
        setCourse(r.data);
        toast.success(publish ? "Published" : "Saved");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addModule = () => {
    setCourse(c => ({ ...c, modules: [...(c.modules || []), { id: `mod_${Math.random().toString(36).slice(2, 10)}`, title: "New module", lessons: [] }] }));
  };

  const removeModule = (mi) => {
    setCourse(c => ({ ...c, modules: c.modules.filter((_, i) => i !== mi) }));
  };

  const updateModule = (mi, patch) => {
    setCourse(c => ({ ...c, modules: c.modules.map((m, i) => i === mi ? { ...m, ...patch } : m) }));
  };

  const addLesson = (mi) => {
    const newLesson = { id: `les_${Math.random().toString(36).slice(2, 10)}`, title: "New lesson", type: "text", content: "", duration_min: 5 };
    setCourse(c => ({ ...c, modules: c.modules.map((m, i) => i === mi ? { ...m, lessons: [...(m.lessons || []), newLesson] } : m) }));
  };

  const updateLesson = (mi, li, patch) => {
    setCourse(c => ({ ...c, modules: c.modules.map((m, i) => i === mi ? { ...m, lessons: m.lessons.map((l, j) => j === li ? { ...l, ...patch } : l) } : m) }));
  };

  const removeLesson = (mi, li) => {
    setCourse(c => ({ ...c, modules: c.modules.map((m, i) => i === mi ? { ...m, lessons: m.lessons.filter((_, j) => j !== li) } : m) }));
  };

  const generateAI = async () => {
    if (!aiGen.topic.trim()) { toast.error("Add a topic first"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate-course", aiGen);
      const data = r.data;
      setCourse(c => ({
        ...c,
        title: data.title || c.title,
        description: data.description || c.description,
        category: data.category || c.category,
        modules: data.modules || [],
      }));
      toast.success("Course generated · review & save");
      setShowAI(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const generateQuiz = async (mi, li) => {
    const lesson = course.modules[mi].lessons[li];
    if (!lesson.content || lesson.content.length < 50) { toast.error("Add some lesson content first"); return; }
    try {
      const r = await api.post("/ai/generate-quiz", { content: lesson.content, num_questions: 5 });
      updateLesson(mi, li, { quiz: r.data, type: lesson.type });
      toast.success("Quiz attached to lesson");
    } catch (err) {
      toast.error("Quiz generation failed");
    }
  };

  if (!course) return <Layout><div className="px-10 py-20">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="px-10 py-10 max-w-5xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-2">{isNew ? "New course" : "Editing"}</div>
            <input
              data-testid="builder-title"
              value={course.title}
              onChange={(e) => setCourse({ ...course, title: e.target.value })}
              className="editorial-h1 text-5xl bg-transparent outline-none w-full border-b border-transparent focus:border-[#0A0A0A] pb-2"
            />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowHelp(true)} data-testid="open-help" className="btn-secondary" title="How to add training"><HelpCircle size={14} strokeWidth={1.5}/> Help</button>
            <button onClick={() => setShowAI(true)} data-testid="open-ai-gen" className="btn-secondary"><Sparkles size={14} strokeWidth={1.5}/> AI Generate</button>
            <button onClick={() => save()} data-testid="save-button" disabled={saving} className="btn-secondary"><Save size={14} strokeWidth={1.5}/> Save</button>
            <button onClick={() => save({ publish: true })} data-testid="publish-button" disabled={saving} className="btn-primary"><Eye size={14} strokeWidth={1.5}/> {course.is_published ? "Update" : "Publish"}</button>
          </div>
        </div>

        {course.course_id && course.is_published && (course.visibility === "org" || course.visibility === "private") && (
          <FreeJoinLinkBlock courseId={course.course_id} />
        )}

        {/* Meta */}
        <div className="editorial-card-static p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="overline block mb-2">Description</label>
            <textarea data-testid="builder-description" rows={3} value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Thumbnail</label>
            <input data-testid="builder-thumbnail" value={course.thumbnail || ""} onChange={(e) => setCourse({ ...course, thumbnail: e.target.value })} placeholder="Paste image URL, or use the cropper below" className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none mb-2" />
            <ThumbnailCropper
              currentUrl={course.thumbnail}
              testid="builder-thumbnail-cropper"
              onUploaded={({ url }) => setCourse(c => ({ ...c, thumbnail: url }))}
            />
            <p className="text-xs text-[#52525B] mt-2">
              Drag the box to crop, or pick <em>Fit whole image</em> so nothing gets chopped off — works great for text-heavy screenshots.
            </p>
            {course.thumbnail && (
              <div className="mt-3">
                <div className="overline text-[#52525B] mb-2">Preview (how it appears on cards)</div>
                <img src={course.thumbnail} alt="thumbnail preview" className="w-full max-w-xs h-32 object-cover border border-[#E4E4E7]" data-testid="thumbnail-preview" />
              </div>
            )}
          </div>
          <div>
            <label className="overline block mb-2">Category</label>
            <input data-testid="builder-category" value={course.category} onChange={(e) => setCourse({ ...course, category: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Price (USD)</label>
            <input data-testid="builder-price" type="number" value={course.price} onChange={(e) => setCourse({ ...course, price: parseFloat(e.target.value || "0") })} className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Visibility</label>
            <select data-testid="builder-visibility" value={course.visibility} onChange={(e) => setCourse({ ...course, visibility: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none bg-white">
              <option value="private">Private (only me)</option>
              <option value="org">Organization only</option>
              <option value="public">Public · Marketplace</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Passing score (%)</label>
            <input data-testid="builder-passing-score" type="number" min={0} max={100} value={course.passing_score ?? 80} onChange={(e) => setCourse({ ...course, passing_score: Math.max(0, Math.min(100, parseInt(e.target.value || "80"))) })} className="w-full px-3 py-2 border border-[#E4E4E7] focus:border-[#0A0A0A] outline-none" />
          </div>
        </div>

        {/* Modules */}
        <div className="space-y-5">
          {course.modules?.map((m, mi) => (
            <details key={m.id} open className="editorial-card-static" data-testid={`builder-module-${mi}`}>
              <summary className="px-5 py-4 cursor-pointer flex items-center justify-between border-b border-[#E4E4E7]">
                <div className="flex items-center gap-3 flex-1">
                  <ChevronDown size={16} strokeWidth={1.5}/>
                  <input value={m.title} onChange={(e) => updateModule(mi, { title: e.target.value })} className="font-serif text-2xl bg-transparent outline-none flex-1" />
                </div>
                <button onClick={(e) => { e.preventDefault(); removeModule(mi); }} className="p-2 hover:bg-[#F9F9F8]" data-testid={`remove-module-${mi}`}><Trash2 size={14}/></button>
              </summary>
              <div className="p-5 space-y-3">
                {m.lessons?.map((l, li) => (
                  <div key={l.id} className="border border-[#E4E4E7] p-4 bg-white" data-testid={`builder-lesson-${mi}-${li}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <input value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} className="font-display text-lg flex-1 bg-transparent outline-none border-b border-transparent focus:border-[#0A0A0A]" />
                      <select value={l.type} onChange={(e) => updateLesson(mi, li, { type: e.target.value })} className="text-xs px-2 py-1 border border-[#E4E4E7]">
                        <option value="text">Text</option>
                        <option value="video">Video</option>
                        <option value="file">File</option>
                      </select>
                      <input type="number" value={l.duration_min} onChange={(e) => updateLesson(mi, li, { duration_min: parseInt(e.target.value || "5") })} className="w-16 text-xs px-2 py-1 border border-[#E4E4E7]" />
                      <button onClick={() => removeLesson(mi, li)} className="p-1 hover:bg-[#F9F9F8]"><Trash2 size={14}/></button>
                    </div>
                    {l.type === "video" && (
                      <>
                        <input value={l.video_url || ""} onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })} placeholder="Video URL (YouTube, Vimeo, MP4) — or upload below" className="w-full px-3 py-2 border border-[#E4E4E7] mb-2 text-sm" />
                        <div className="mb-2">
                          <FileUpload
                            accept="video/*"
                            label="Upload video"
                            currentUrl={l.video_url}
                            kind="video"
                            testid={`upload-video-${mi}-${li}`}
                            onUploaded={({ url }) => updateLesson(mi, li, { video_url: url })}
                          />
                        </div>
                      </>
                    )}
                    {l.type === "file" && (
                      <>
                        <input value={l.file_url || ""} onChange={(e) => updateLesson(mi, li, { file_url: e.target.value })} placeholder="File URL (PDF, SCORM zip) — or upload below" className="w-full px-3 py-2 border border-[#E4E4E7] mb-2 text-sm" />
                        <div className="mb-2">
                          <FileUpload
                            accept="application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/*"
                            label="Upload file"
                            currentUrl={l.file_url}
                            kind="file"
                            testid={`upload-file-${mi}-${li}`}
                            onUploaded={({ url, filename }) => updateLesson(mi, li, { file_url: url, file_name: filename })}
                          />
                        </div>
                      </>
                    )}
                    <textarea value={l.content} onChange={(e) => updateLesson(mi, li, { content: e.target.value })} rows={4} placeholder="Lesson content (markdown supported)" className="w-full px-3 py-2 border border-[#E4E4E7] text-sm font-mono-jb" />
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => generateQuiz(mi, li)} className="text-xs flex items-center gap-1 hover:text-[#E63946]"><Wand2 size={12}/> Generate quiz from content</button>
                      {l.quiz && <span className="overline text-[#166534]">✓ Quiz attached ({l.quiz.questions?.length || 0} Qs)</span>}
                    </div>
                  </div>
                ))}
                <button onClick={() => addLesson(mi)} className="btn-secondary text-xs" data-testid={`add-lesson-${mi}`}><Plus size={12}/> Add lesson</button>
              </div>
            </details>
          ))}
          <button onClick={addModule} data-testid="add-module" className="btn-secondary w-full justify-center"><Plus size={14}/> Add module</button>
        </div>

        {showHelp && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-8">
            <div className="bg-white max-w-2xl w-full border border-[#0A0A0A] max-h-[92vh] overflow-auto" data-testid="help-modal">
              <div className="px-8 py-6 border-b border-[#E4E4E7] flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <div className="overline text-[#E63946]">Quick start</div>
                  <h2 className="font-serif text-3xl">How to add training</h2>
                </div>
                <button onClick={() => setShowHelp(false)} data-testid="close-help" className="p-2 hover:bg-[#F9F9F8]"><X size={16}/></button>
              </div>
              <div className="px-8 py-6 space-y-6 text-sm text-[#3F3F46] leading-relaxed">
                <Step n="1" title="Title & description">
                  Click the big title up top and type the course name. Add a short description in the box below — what learners will get out of this.
                </Step>
                <Step n="2" title="Thumbnail (the cover image)">
                  Click <strong>Choose &amp; crop thumbnail</strong>. Pick any image — even a tall screenshot or wide chart.
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li><strong>Crop to 3:2</strong> — drag the box to choose the part you want shown.</li>
                    <li><strong>Fit whole image</strong> — keeps every pixel, adds colored bars on the sides. Best for text-heavy screenshots.</li>
                  </ul>
                </Step>
                <Step n="3" title="Add modules and lessons">
                  Hit <strong>Add module</strong> (a module groups related lessons). Inside, hit <strong>Add lesson</strong>. Pick a lesson type:
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li><strong>Text</strong> — paste your written content (markdown works).</li>
                    <li><strong>Video</strong> — paste a YouTube/Vimeo link, or upload an MP4 directly (up to 2GB).</li>
                    <li><strong>File</strong> — upload a PDF, PowerPoint, or image learners can download.</li>
                  </ul>
                </Step>
                <Step n="4" title="Add a quiz (optional but recommended)">
                  Inside any lesson, write your content first, then click <strong>Generate quiz from content</strong>. The AI builds a 5-question quiz tied to that lesson. Pass score is set at the top right (default 80%).
                </Step>
                <Step n="5" title="Pick who can see it">
                  Set <strong>Visibility</strong>:
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li><strong>Private</strong> — only you.</li>
                    <li><strong>Organization</strong> — your company / paying companies.</li>
                    <li><strong>Public</strong> — listed in the marketplace.</li>
                  </ul>
                </Step>
                <Step n="6" title="Save → Publish → Share">
                  Hit <strong>Save</strong> as you go. When ready, click <strong>Publish</strong>. A <em>Free join link</em> block appears at the top — copy that link and send it to a company admin so their team can self-enroll. You see all their completion + pass/fail data automatically.
                </Step>
                <div className="pt-4 border-t border-[#E4E4E7] flex justify-end">
                  <button onClick={() => setShowHelp(false)} className="btn-primary text-xs" data-testid="help-got-it">Got it</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showAI && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
            <div className="bg-white max-w-lg w-full border border-[#0A0A0A] p-8" data-testid="ai-gen-modal">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="overline text-[#E63946]">AI Studio</div>
                  <h2 className="font-serif text-3xl">Generate a course</h2>
                </div>
                <button onClick={() => setShowAI(false)} className="p-2">×</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="overline block mb-2">Topic</label>
                  <input data-testid="ai-topic" value={aiGen.topic} onChange={(e) => setAiGen({ ...aiGen, topic: e.target.value })} placeholder="e.g. Crisis communication for ops leaders" className="w-full px-3 py-2 border border-[#E4E4E7]" />
                </div>
                <div>
                  <label className="overline block mb-2">Audience</label>
                  <input value={aiGen.audience} onChange={(e) => setAiGen({ ...aiGen, audience: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7]" />
                </div>
                <div>
                  <label className="overline block mb-2">Length</label>
                  <select value={aiGen.duration} onChange={(e) => setAiGen({ ...aiGen, duration: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] bg-white">
                    <option value="short">Short · 3 modules</option>
                    <option value="medium">Medium · 5 modules</option>
                    <option value="long">Long · 8 modules</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Notes (optional)</label>
                  <textarea rows={3} value={aiGen.notes} onChange={(e) => setAiGen({ ...aiGen, notes: e.target.value })} placeholder="Anything specific to include…" className="w-full px-3 py-2 border border-[#E4E4E7]" />
                </div>
                <button onClick={generateAI} data-testid="ai-generate-submit" disabled={aiLoading} className="btn-primary w-full justify-center">
                  {aiLoading ? "Generating… (15-30s)" : <><Sparkles size={14}/> Generate</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 bg-[#0A0A0A] text-white font-serif text-lg flex items-center justify-center">{n}</div>
      <div className="flex-1">
        <h3 className="font-display text-base font-medium mb-1">{title}</h3>
        <div>{children}</div>
      </div>
    </div>
  );
}

function FreeJoinLinkBlock({ courseId }) {
  const [lic, setLic] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/courses/${courseId}/free-join-link`);
      setLic(r.data);
      toast.success("Free join link ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create link");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    const url = `${window.location.origin}/join/${lic.join_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="editorial-card-static p-6 mb-6 bg-[#0A0A0A] text-white" data-testid="free-join-link-block">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#E63946] mb-1">Share with the company</div>
          <h3 className="font-serif text-2xl">Free join link</h3>
          <p className="text-sm text-zinc-400 mt-2 max-w-md">Generate a link the company admin can share with every employee. They self-enroll, you keep all completion + scoring data.</p>
        </div>
        {!lic ? (
          <button onClick={generate} disabled={loading} data-testid="generate-free-link" className="btn-primary bg-[#E63946] border-[#E63946] hover:bg-[#D62828]">
            <LinkIcon size={14} strokeWidth={1.5}/> {loading ? "Creating…" : "Generate join link"}
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="px-3 py-2 bg-white text-[#0A0A0A] border border-white font-mono-jb text-xs">
              {window.location.origin}/join/{lic.join_code}
            </div>
            <button onClick={copy} data-testid="copy-free-link" className="p-2 border border-zinc-700 hover:bg-zinc-800"><Copy size={14}/></button>
          </div>
        )}
      </div>
    </div>
  );
}
